// Shared "Add a custom product" form for the add-product window — used by
// the presentation and by the estimate / quote (Stephanie 2026-10-14).
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Request failed (${err.status}).`;
  }
  return "Request failed. Try again or check your connection.";
}

// ─── Custom (manual, off-feed) product form ───────────────────────────
// Creates a supplier (optionally new) + a manual product, then hands it to
// the caller's onCreated (the presentation adds it as an item; the estimate /
// quote adds it as a line — Stephanie 2026-10-14: "if the item isn't in our
// system yet, we need to be able to add it from here"). The new product is
// searchable in the builder but excluded from the public storefront
// (source='internal', 2026-07-14).

export interface CreatedCustomProduct {
  product_id: string;
  name: string;
  sku: string | null;
  supplierName: string;
  /** Typed cost, or null when left blank (never a made-up number). */
  cost: string | null;
  sellPrice: string | null;
  /** What the rep typed in Description, or null. */
  description: string | null;
}

export function CustomProductForm(props: {
  /** Adds the just-created product to the document. Return a warning to show
   *  (e.g. "added but not filed under that section"), or null. */
  onCreated: (created: CreatedCustomProduct) => Promise<string | null>;
  /** "Added to the presentation." / "Added to the quote." */
  addedText: string;
  /** Button label, e.g. "Add to presentation". */
  addLabel: string;
  onItemAdded: () => void;
  onClose: () => void;
}) {
  const { onCreated, addedText, addLabel, onItemAdded, onClose } = props;
  const queryClient = useQueryClient();
  const NEW_SUPPLIER = "__new__";
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [newSupplierName, setNewSupplierName] = useState("");
  const [newSupplierEmail, setNewSupplierEmail] = useState("");
  const [cost, setCost] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  // Success feedback, peach not rust — a confirmation must never wear failure
  // styling. Cleared when the rep edits the form again so it cannot go stale.
  const [addedNotice, setAddedNotice] = useState<string | null>(null);

  const vendorsQ = useQuery({
    queryKey: ["vendors", "list"],
    queryFn: () => api<{ rows: Array<{ id: string; name: string }> }>("/vendors"),
    staleTime: 5 * 60 * 1000,
  });

  const create = useMutation({
    mutationFn: async () => {
      // 1. Resolve the supplier — create it first when "New supplier".
      let resolvedVendorId = vendorId;
      if (vendorId === NEW_SUPPLIER) {
        const v = await api<{ vendor: { id: string } }>("/vendors", {
          method: "POST",
          body: JSON.stringify({
            name: newSupplierName.trim(),
            primary_email: newSupplierEmail.trim() || undefined,
          }),
        });
        resolvedVendorId = v.vendor.id;
        // Pin the just-created vendor immediately (before the product/add-item
        // steps that can still fail) so a retry reuses it instead of creating
        // a DUPLICATE supplier. Refresh the dropdown so it shows the new row.
        setVendorId(resolvedVendorId);
        void queryClient.invalidateQueries({ queryKey: ["vendors", "list"] });
      }
      // 2. Create the manual product (products + merchd_products + offer trio).
      const created = await api<{ product_id: string }>(
        "/catalog/manual-products",
        {
          method: "POST",
          body: JSON.stringify({
            name: name.trim(),
            sku: sku.trim() || undefined,
            vendor_id: resolvedVendorId,
            // Omit entirely when blank so the API stores NULL (unpriced)
            // rather than a fabricated number. Never send "0".
            ...(cost.trim() === "" ? {} : { cost: cost.trim() }),
            sell_price: sellPrice.trim() || undefined,
            description: description.trim() || undefined,
          }),
        },
      );
      // 3. Add it to the document (the caller decides how).
      const supplierName =
        vendorId === NEW_SUPPLIER
          ? newSupplierName.trim()
          : (vendorsQ.data?.rows ?? []).find((v) => v.id === resolvedVendorId)?.name ?? "";
      const warning = await onCreated({
        product_id: created.product_id,
        name: name.trim(),
        sku: sku.trim() || null,
        supplierName,
        cost: cost.trim() === "" ? null : cost.trim(),
        sellPrice: sellPrice.trim() || null,
        description: description.trim() || null,
      });
      if (warning) setFormError(warning);
    },
    onSuccess: () => {
      // Tell the rep it worked BEFORE the form collapses. The catalog-search
      // path greys the picked row "Added" and keeps a running "N added" banner;
      // this path just vanished, so a rep who had typed a custom product had no
      // signal it landed — or that it was filed under the category they clicked
      // Add from — and had to close the whole modal to find out.
      setAddedNotice(addedText);
      // CLEAR THE PRODUCT FIELDS. The copy above invites "add another", and
      // before this the form kept every value: clicking Add again with nothing
      // changed silently created a SECOND identical custom product on a live
      // client-facing presentation, and a rep adding a genuinely different
      // product could carry the previous one's SKU, cost and description across
      // by only retyping the name. The old behaviour closed the modal after one
      // add, so neither trap existed until the confirmation was introduced.
      //
      // vendorId is deliberately KEPT — adding several products from the same
      // supplier in a row is the case the copy is inviting, and re-picking the
      // supplier every time would be the annoyance that made a rep stop reading
      // the form at all.
      setName("");
      setSku("");
      setCost("");
      setSellPrice("");
      setDescription("");
      onItemAdded();
    },
    onError: (err) => setFormError(extractError(err)),
  });

  const onSubmit = (): void => {
    setFormError(null);
    if (!name.trim()) {
      setFormError("Product name is required.");
      return;
    }
    if (!vendorId) {
      setFormError("Pick a supplier or add a new one.");
      return;
    }
    if (vendorId === NEW_SUPPLIER && !newSupplierName.trim()) {
      setFormError("New supplier name is required.");
      return;
    }
    // Cost is OPTIONAL as of 2026-08-11 (GM Stephanie: it "should not be
    // required as there are multiple price breaks"). Blank = price it on the
    // document. A TYPED value must still be a positive number — blank means
    // "not known yet", zero would mean "free", and the two must not collapse.
    const costTrimmed = cost.trim();
    if (costTrimmed !== "") {
      if (!/^\d+(\.\d{1,3})?$/.test(costTrimmed) || Number(costTrimmed) <= 0) {
        setFormError("Cost must be a number greater than 0, or leave it blank.");
        return;
      }
    }
    create.mutate();
  };

  return (
    <div className="rounded-md border border-peach-300/70 bg-peach-50/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-brown-500">
          Add a custom product
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-brown-300 hover:text-brown-500"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="text-xs font-semibold text-brown-400">
          Product name*
          <Input
            value={name}
            onChange={(e) => {
              setAddedNotice(null);
              setName(e.target.value);
            }}
            placeholder="e.g. Custom embroidered beanie"
            className="mt-1"
          />
        </label>
        <label className="text-xs font-semibold text-brown-400">
          SKU
          <Input
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder="Optional"
            maxLength={120}
            className="mt-1"
          />
        </label>
        <label className="text-xs font-semibold text-brown-400">
          Supplier*
          <select
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            className="mt-1 h-9 w-full rounded-md border border-brown-100 bg-white px-3 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
            aria-label="Supplier"
          >
            <option value="">Select supplier…</option>
            {(vendorsQ.data?.rows ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
            <option value={NEW_SUPPLIER}>+ New supplier…</option>
          </select>
        </label>
        <label className="text-xs font-semibold text-brown-400">
          Cost
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.001"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="Optional — leave blank for price breaks"
            className="mt-1"
          />
        </label>
        {vendorId === NEW_SUPPLIER && (
          <>
            <label className="text-xs font-semibold text-brown-400">
              New supplier name*
              <Input
                value={newSupplierName}
                onChange={(e) => setNewSupplierName(e.target.value)}
                placeholder="Supplier name"
                className="mt-1"
              />
            </label>
            <label className="text-xs font-semibold text-brown-400">
              New supplier email
              <Input
                type="email"
                value={newSupplierEmail}
                onChange={(e) => setNewSupplierEmail(e.target.value)}
                placeholder="Optional — for PO send"
                className="mt-1"
              />
            </label>
          </>
        )}
        <label className="text-xs font-semibold text-brown-400">
          Sell price
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={sellPrice}
            onChange={(e) => setSellPrice(e.target.value)}
            placeholder="Optional"
            className="mt-1"
          />
        </label>
        <label className="text-xs font-semibold text-brown-400 sm:col-span-2">
          Description
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            maxLength={20_000}
            className="mt-1"
          />
        </label>
      </div>
      {formError && (
        <p className="mt-2 text-sm text-rust-600">{formError}</p>
      )}
      {addedNotice && !formError && (
        <p className="mt-2 rounded-lg border border-peach-300 bg-peach-100 px-3 py-2 text-sm text-brown-500">
          {addedNotice} Add another, or close when you're done.
        </p>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          type="button"
          onClick={onSubmit}
          disabled={create.isPending}
        >
          {create.isPending ? "Adding…" : addLabel}
        </Button>
      </div>
    </div>
  );
}

